import { useState, useEffect } from 'react';
import { NewsPost, Example, Page, FilterOptions } from '../types/content';
import { 
  sortByDate,
  filterByTopics
} from '../lib/content';
import { 
  loadNewsPosts, 
  loadExamples, 
  loadPage,
  searchContent
} from '../lib/contentLoader';

/**
 * Hook for loading and managing news posts
 */
export function useNews(filters?: FilterOptions) {
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      setLoading(true);
      const allPosts = await loadNewsPosts();
      let filteredPosts = allPosts;

      if (filters?.topics && filters.topics.length > 0) {
        filteredPosts = filterByTopics(filteredPosts, filters.topics);
      }

      setPosts(sortByDate(filteredPosts));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  return { posts, loading, error, reload: loadNews };
}

/**
 * Hook for loading and managing examples
 */
export function useExamples(filters?: FilterOptions) {
  const [examples, setExamples] = useState<Example[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadExamplesData();
  }, []);

  const loadExamplesData = async () => {
    try {
      setLoading(true);
      const allExamples = await loadExamples();
      let filteredExamples = allExamples;

      if (filters?.goal && filters.goal.length > 0) {
        filteredExamples = filteredExamples.filter(ex => filters.goal!.includes(ex.goal));
      }

      if (filters?.audience && filters.audience.length > 0) {
        filteredExamples = filteredExamples.filter(ex => filters.audience!.includes(ex.audience));
      }

      if (filters?.complexity && filters.complexity.length > 0) {
        filteredExamples = filteredExamples.filter(ex => filters.complexity!.includes(ex.complexity));
      }

      setExamples(filteredExamples);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load examples');
    } finally {
      setLoading(false);
    }
  };

  return { examples, loading, error, reload: loadExamplesData };
}

/**
 * Hook for loading a single page
 */
export function usePage(slug: string) {
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPageData(slug);
  }, [slug]);

  const loadPageData = async (pageSlug: string) => {
    try {
      setLoading(true);
      const pageData = await loadPage(pageSlug);
      setPage(pageData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  return { page, loading, error, reload: () => loadPageData(slug) };
}

/**
 * Hook for search functionality
 */
export function useSearch(query: string) {
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }

    const performSearch = async () => {
      setLoading(true);
      try {
        const searchResults = await searchContent(query);
        setResults(searchResults);
      } catch (error) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(performSearch, 300);
    return () => clearTimeout(debounce);
  }, [query]);

  return { results, loading };
}